import { useSiteImage } from "@/hooks/useSiteImages";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicNav, PublicFooter } from "@/components/PublicChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GraduationCap, Clock, Award, Users, CreditCard, CheckCircle2 } from "lucide-react";

export default function TrainTheTrainer() {
  const imgTT = useSiteImage("train_the_trainer", "https://customer-assets-cm19k8pv.emergentagent.net/job_care-cert-hub/artifacts/1bbedb18ede23ca6_AaE-seOx4Qd1p7Y_2P8-fg-AaE-seOxdn-yHPGxNMI0Lg.jpg");
  const nav = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [application, setApplication] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", care_home: "", motivation: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlan(r.data.find((p) => p.lookup_key === "tt_course"))).catch(() => {});
    if (user) {
      setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
      api.get("/train-the-trainer/me").then((r) => setApplication(r.data)).catch(() => {});
    }
  }, [user]);

  const apply = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Please log in or start a trial to apply."); nav("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/train-the-trainer/apply", form);
      setApplication(data);
      toast.success("Application submitted! We'll review it shortly.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: "tt_course", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      <PublicNav />
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="label-caps text-coral">Premium · one-off · application-gated</span>
            <h1 className="text-4xl md:text-5xl font-bold text-brand tracking-tight mt-3">Train the Trainer</h1>
            <p className="text-lg text-muted-foreground mt-4 leading-relaxed">Our Train the Trainer course gives a nominated care home staff member the skills and confidence to deliver effective oral care training to colleagues. Tailored to your care home and residents’ needs, the course includes a full day of one-to-one training with a dental professional and a comprehensive training kit.</p>
            <p className="text-lg text-muted-foreground mt-3 leading-relaxed">Clear, practical and easy to deliver, the training includes optional case scenarios, thought-provoking questions and hands-on activities to keep staff engaged.</p>
            <p data-testid="tt-not-included-note" className="mt-4 inline-flex items-start gap-2 rounded-xl bg-coral/10 border border-coral/30 px-4 py-3 text-sm text-brand"><span className="font-semibold text-coral shrink-0">Please note:</span><span>Train the Trainer is a separate, one-off professional qualification and is <b>not part of the KOHC subscription</b>. Places are by application.</span></p>
            <div className="flex flex-wrap gap-6 mt-8">
              <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-brand" /><span className="text-sm">6 CPD hours</span></div>
              <div className="flex items-center gap-2"><Award className="h-5 w-5 text-brand" /><span className="text-sm">Own certificate</span></div>
              <div className="flex items-center gap-2"><Users className="h-5 w-5 text-brand" /><span className="text-sm">Roster route</span></div>
            </div>
            <div className="mt-8 text-4xl font-bold text-brand font-heading">{plan ? `£${plan.amount}` : "£750"} <span className="text-base font-normal text-muted-foreground">one-off</span></div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-cream-deep shadow-xl">
            <img src={imgTT} alt="A learner following a KOHC oral health webinar on aspiration pneumonia on her laptop" className="w-full h-[400px] object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-20">
        <div className="bg-cream-card border border-cream-deep rounded-3xl p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6"><GraduationCap className="h-6 w-6 text-coral" /><h2 className="text-2xl font-bold text-brand">Apply for the next cohort</h2></div>

          {application ? (
            <div data-testid="tt-application-status">
              {application.status === "pending" && <Banner icon={Clock} tone="amber" title="Application received" text="Your application is under review. You'll be notified once approved, then you can complete payment." />}
              {(application.status === "approved") && (
                <>
                  <Banner icon={CheckCircle2} tone="green" title="Approved!" text="Congratulations — your application is approved. Complete payment to secure your place." />
                  {!application.paid && <Button data-testid="tt-checkout-btn" onClick={checkout} disabled={loading} className="mt-5 h-12 px-8 rounded-full bg-coral hover:bg-coral-hover text-white"><CreditCard className="h-4 w-4 mr-2" />Pay £{plan?.amount || 750} & enrol</Button>}
                  {application.paid && <Banner icon={Award} tone="green" title="Enrolled" text="You're enrolled. Your certificate will be issued on completion." />}
                </>
              )}
              {application.status === "graduated" && <Banner icon={Award} tone="green" title="Certified trainer" text="You are a certified Train-the-Trainer graduate and on the roster." />}
              {application.status === "rejected" && <Banner icon={Clock} tone="red" title="Not this cohort" text="Your application wasn't successful this time. Please contact us to reapply." />}
              {application.paid && !["graduated"].includes(application.status) && <p className="text-sm text-muted-foreground mt-4">Payment confirmed. An admin will complete your enrolment and certification.</p>}
            </div>
          ) : (
            <form onSubmit={apply} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Full name</Label><Input data-testid="tt-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input data-testid="tt-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div><Label>Care home / organisation</Label><Input data-testid="tt-care-home" required value={form.care_home} onChange={(e) => setForm({ ...form, care_home: e.target.value })} /></div>
              <div><Label>Why do you want to become a trainer?</Label><Textarea data-testid="tt-motivation" required rows={4} value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} /></div>
              <Button data-testid="tt-apply-btn" type="submit" disabled={loading} className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-white">{loading ? "Submitting…" : "Submit application"}</Button>
              <p className="text-xs text-muted-foreground text-center">Payment is only taken after your application is approved.</p>
            </form>
          )}
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}

function Banner({ icon: Icon, tone, title, text }) {
  const tones = { green: "bg-rag-green-bg text-rag-green", amber: "bg-rag-amber-bg text-rag-amber", red: "bg-rag-red-bg text-rag-red" };
  return (
    <div className={`rounded-xl p-5 flex gap-3 ${tones[tone]}`}>
      <Icon className="h-6 w-6 flex-shrink-0" />
      <div><div className="font-bold">{title}</div><p className="text-sm opacity-90 mt-0.5">{text}</p></div>
    </div>
  );
}
